import type { Vec2 } from '../types';

/**
 * Keyboard-based tilt simulation for desktop development.
 * W/S = forward/back tilt, J/; = left/right tilt.
 * (A/L are reserved for flippers in TouchInput)
 */
export class KeyboardTilt {
  readonly tilt: Vec2 = { x: 0, y: 0 };

  private keys = {
    up: false,
    down: false,
    left: false,
    right: false,
  };

  /** How fast tilt changes (units per second) */
  acceleration = 4.0;
  /** How fast tilt returns to center */
  friction = 6.0;

  constructor() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    switch (e.code) {
      case 'KeyW':
        this.keys.up = true;
        break;
      case 'KeyS':
        this.keys.down = true;
        break;
      case 'KeyJ':
        this.keys.left = true;
        break;
      case 'Semicolon':
        this.keys.right = true;
        break;
    }
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    switch (e.code) {
      case 'KeyW':
        this.keys.up = false;
        break;
      case 'KeyS':
        this.keys.down = false;
        break;
      case 'KeyJ':
        this.keys.left = false;
        break;
      case 'Semicolon':
        this.keys.right = false;
        break;
    }
  };

  /** Call each fixed update step */
  update(dt: number): void {
    // Target based on keys
    let targetX = 0;
    let targetY = 0;

    if (this.keys.left) targetX -= 1;
    if (this.keys.right) targetX += 1;
    if (this.keys.up) targetY -= 1; // Up tilts ball "forward"
    if (this.keys.down) targetY += 1;

    // Apply acceleration toward target or friction toward zero
    if (targetX !== 0) {
      this.tilt.x += targetX * this.acceleration * dt;
    } else {
      this.tilt.x -= this.tilt.x * this.friction * dt;
    }

    if (targetY !== 0) {
      this.tilt.y += targetY * this.acceleration * dt;
    } else {
      this.tilt.y -= this.tilt.y * this.friction * dt;
    }

    // Clamp
    this.tilt.x = Math.max(-1, Math.min(1, this.tilt.x));
    this.tilt.y = Math.max(-1, Math.min(1, this.tilt.y));
  }

  dispose(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
  }
}
